import React, { useState } from 'react';
import SalesTable from './SalesTable';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import '../css/FileUpload.css';

const API_URL = process.env.REACT_APP_API_URL;


const FileUpload = () => {
  const [files, setFiles] = useState([]);
  const [reports, setReports] = useState([]);
  const [salesByClass, setSalesByClass] = useState(null);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState('');
  const [location, setLocation] = useState('');
  const [periodDate, setPeriodDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [defaultProductType, setDefaultProductType] = useState("");
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [reportPeriodDate, setReportPeriodDate] = useState('');

  const handleDateChange = (update) => {
    setDateRange(update);
    if (update[0] && update[1]) {

      // Format dates for API
      const formatApiDate = (date) => {
        return date.toLocaleDateString('en-CA'); // YYYY-MM-DD format
      };

    
      const apiStart = formatApiDate(update[0]);
      const apiEnd = formatApiDate(update[1]);

      setReportPeriodDate(`${apiStart} to ${apiEnd}`);
  
    }
  };

  const handleFindReplace = () => {
    if (!salesByClass || !findText) return;
    const updatedSalesData = salesByClass.map(sale => ({
      ...sale,
      location: sale.location.replace(new RegExp(findText, 'gi'), replaceText)
    }));
    setSalesByClass(updatedSalesData);
  };

  const handleFileChange = (e) => {
    setError(null);
    setFiles(Array.from(e.target.files));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select files to upload');
      return;
    }

    if (!startDate || !endDate) {
      setError('Please select a date range');
      return;
    }

    if (!company.trim()) {
      setError('Please enter a company name');
      return;
    }

    const formData = new FormData();
    formData.append('company', company);
    formData.append('defaultProductType', defaultProductType || "30");
   
     
    if (startDate && endDate) {
      const formatYearMonth = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
      };

      formData.append('startDate', formatYearMonth(startDate));
      formData.append('endDate', formatYearMonth(endDate));
    }
    files.forEach(file => formData.append('files', file));

    try {
      setIsLoading(true);
      const response = await axios.post(`${API_URL}/api/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setReports(response.data.reports);
      setSalesByClass(response.data.salesByClass);
      setPeriodDate(response.data.periodDate);
      setError(null);
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="file-upload-container">
      <div className="project-description">
        <h1>POS Report Generator</h1>
        <p>Upload CSV files to create a comprehensive Point of Sale (POS) report.</p>
        <br />
        <p><strong>Steps:</strong></p>
        <br />
        <ol>
          <li>Enter the company name</li>
          <li>Set default Class Code for Blank Product Type, Default is 30</li>
          <li>Select date range</li>
          <li>Upload your CSV files (sales, inventory, mapping, etc.)</li>
        </ol>
      </div>

      <div className="upload-section">
        <h2>Upload CSV Files</h2>
        <div className="form-group">
          <input
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className="text-input"
            placeholder="Enter company name"
            required
          />
          <input
            type="text"
            value={defaultProductType}
            onChange={(e) => setDefaultProductType(e.target.value)}
            className="text-input"
            placeholder="Enter Class Code for Blank Product Type"
            required
          />
        </div>

        <div className="upload-controls">

        <div className="date-picker-container">
          <DatePicker
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
            className="date-range-input"
            placeholderText="Select date range..."
            dateFormat="MMMM d, yyyy"  // Updated format
            isClearable={true}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
          />
        </div>
        
          <input 
            type="file" 
            accept=".csv" 
            multiple 
            onChange={handleFileChange}
            className="file-input"
            disabled={isLoading}
          />
          <button 
            onClick={handleUpload}
            className="upload-button"
            disabled={files.length === 0 || isLoading}
          >
            {isLoading ? 'Processing...' : 'Upload Files'}
          </button>
        </div>

        {isLoading && (
          <div className="loading-container">
            <div className="loading-bar">
              <div className="loading-progress" />
            </div>
            <p className="loading-text">Processing files, please wait...</p>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        {files.length > 0 && (
          <div className="selected-files">
            <h4>Selected Files:</h4>
            <ul>
              {files.map((file, index) => (
                <li key={index}>{file.name}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {salesByClass && (
        <section className="find-replace-section">
          <h3>Find and Replace Location Names</h3>
          <div className="input-group">
            <input
              type="text"
              name="findText"
              value={findText}
              onChange={(e) => setFindText(e.target.value)}
              className="text-input"
              placeholder="Find text in location names..."
            />
            <input
              type="text"
              name="replaceText"
              value={replaceText}
              onChange={(e) => setReplaceText(e.target.value)}
              className="text-input"
              placeholder="Replace with..."
            />
            <button 
              onClick={handleFindReplace}
              className="replace-button"
              disabled={!findText}
            >
              Replace All
            </button>
          </div>
        </section>
      )}

      {salesByClass && (
        <div className="sales-table-section">
          <SalesTable 
            salesData={salesByClass}
            company={company}
            periodDate={periodDate}
            dateRange ={reportPeriodDate}
          />
        </div>
      )}
    </div>
  );
};

export default FileUpload;
